/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import './DesktopCalculator.scss';
import CalculatorCardCollection from '../cards/CalculatorCardCollection';
import { Size, Button, ButtonType, Icon } from '@vaisala-rockhopper/components';
import { Row, Col, Container } from 'react-grid-system';
import { VaiIcon } from '@vaisala-rockhopper/design-tokens';
import { useTranslation } from 'react-i18next';
import { CalculatorResult, ParameterModifier, ParameterName } from '../../../modules/calculator/calculatorTypes';
import { ScrollNavContext } from '../../context/ScrollNavContext';
import { handleScroll } from '../navigation/ScrollNav';
import { ModalType } from '../modals/Modals';
import { OptionsConfig } from '../modals/OptionsModal';
import { UnitPreset } from '../../../modules/units/unitTypes';

interface DesktopCalculatorProps {
  activeMethod: number;
  onParameterChange: (cardIndex: number, parameterName: ParameterName, value: ParameterModifier, phase?: number) => void;
  currentResult?: CalculatorResult;
  theme: string;
  onMethodSelect: (method: number) => void;
  onReset: () => void;
  openModal: (type: ModalType, title?: string, optionsConfig?: OptionsConfig, onAccept?: VoidFunction, onReject?: VoidFunction) => void;
  currentUnitPreset: UnitPreset | undefined;
}

const DesktopCalculator = ({
  activeMethod,
  onParameterChange,
  currentResult,
  theme,
  onReset,
  onMethodSelect,
  openModal,
  currentUnitPreset,
}: DesktopCalculatorProps) => {
  const { t } = useTranslation();
  const [, setActivePhase] = React.useContext(ScrollNavContext);
  const [containerHeight, setContainerHeight] = React.useState<number>(0);
  const [lastCardHeight, setLastCardHeight] = React.useState<number>(0);
  const container = React.useCallback((node) => {
    if (node !== null) {
      const height = node.getBoundingClientRect().height;
      setContainerHeight(height);
    }
  }, []);
  const lastCard = React.useCallback((node) => {
    if (node !== null) {
      const height = node.getBoundingClientRect().height;
      setLastCardHeight(height);
    }
  }, []);

  return (
    <>
      <div className={`desktop-calculator-controls ${activeMethod > -1 ? 'desktop-calculator-controls--active' : ''}`}>
        <Container>
          <Row>
            <Col sm={6}>
              <Button
                buttonType={ButtonType.Link}
                buttonSize={Size.S}
                startIcon={<Icon name={VaiIcon.ChevronLeft} />}
                onClick={() => {
                  onMethodSelect(-1);
                }}
              >
                {t('ui.method.changeMethod')}
              </Button>
            </Col>
            <Col sm={6} className="desktop-calculator__reset">
              <Button buttonType={ButtonType.Link} buttonSize={Size.S} startIcon={<Icon name={VaiIcon.Refresh} />} onClick={onReset}>
                {t('ui.reset')}
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
      <div
        onScroll={(e: any) => {
          handleScroll(e, setActivePhase);
        }}
        id="desktop-calculator"
        className={`desktop-calculator ${activeMethod > -1 ? 'desktop-calculator--active' : ''} ${theme}`}
        ref={container}
        style={{ paddingBottom: `calc(${containerHeight}px - ${lastCardHeight}px)` }}
      >
        <Container>
          <div className="desktop-calculator__calculator-area">
            <CalculatorCardCollection
              onParameterChange={onParameterChange}
              currentResult={currentResult}
              activeMethod={activeMethod}
              openModal={openModal}
              ref={lastCard}
              currentUnitPreset={currentUnitPreset}
              theme={theme}
            />
          </div>
        </Container>
      </div>
    </>
  );
};
export default DesktopCalculator;
